export type SchedulerRefreshReason =
  | 'app_open'
  | 'unit_change'
  | 'week_change'
  | 'tab_focus'
  | 'visibility_return'
  | 'post_mutation'
  | 'poll'
  | 'manual';

export type SchedulerRefreshRequestMetadata = {
  requestSequence?: number;
};

export type SchedulerRefreshRequest = SchedulerRefreshRequestMetadata & {
  reason: SchedulerRefreshReason;
};

export type SchedulerRefreshContext = SchedulerRefreshRequest & {
  unitIds: string[];
  signal: AbortSignal;
};

export type SchedulerRefreshDiagnosticsEvent = {
  type: 'start' | 'complete' | 'skipped' | 'deduped' | 'failed' | 'timeout';
  reason: SchedulerRefreshReason;
  duration?: number;
  affectedUnitIds: string[];
};

export type SchedulerRefreshDiagnosticsState = {
  inFlight: boolean;
  currentReason: SchedulerRefreshReason | null;
  pendingReason: SchedulerRefreshReason | null;
  startedAt: number | null;
  lastCompletedAt: number | null;
  lastFailedAt: number | null;
  lastTimeoutAt: number | null;
  lastDurationMs: number | null;
  lastSkipReason: SchedulerRefreshReason | null;
};

export class SchedulerRefreshCancelledError extends Error {
  constructor(message = 'Scheduler refresh cancelled') {
    super(message);
    this.name = 'SchedulerRefreshCancelledError';
  }
}

class SchedulerRefreshTimeoutError extends Error {
  readonly reason: SchedulerRefreshReason;
  readonly timeoutMs: number;

  constructor(reason: SchedulerRefreshReason, timeoutMs: number) {
    super(`Scheduler refresh timed out after ${timeoutMs}ms (${reason})`);
    this.reason = reason;
    this.timeoutMs = timeoutMs;
    this.name = 'SchedulerRefreshTimeoutError';
  }
}

type SchedulerRefreshControllerOptions = {
  refresh: (context: SchedulerRefreshContext) => Promise<void>;
  getUnitIds: () => string[];
  now?: () => number;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
  timeoutMs?: number;
  onTimeoutCleanup?: (reason: SchedulerRefreshReason) => void;
  diagnostics?: (event: SchedulerRefreshDiagnosticsEvent) => void;
};

const FOCUS_STALE_MS = 15_000;
const POLL_BACKOFF_MS = 20_000;
const POST_MUTATION_COALESCE_MS = 50;
export const SCHEDULER_POLL_INTERVAL_MS = POLL_BACKOFF_MS;
export const SCHEDULER_REFRESH_TIMEOUT_MS = 30_000;

const REFRESH_REASON_PRIORITY: Record<SchedulerRefreshReason, number> = {
  post_mutation: 70,
  unit_change: 60,
  week_change: 50,
  app_open: 40,
  visibility_return: 30,
  tab_focus: 30,
  manual: 20,
  poll: 10,
};

export const coalescePendingSchedulerRefreshReason = (
  currentReason: SchedulerRefreshReason | null,
  incomingReason: SchedulerRefreshReason
): SchedulerRefreshReason => {
  if (!currentReason) return incomingReason;
  const currentPriority = REFRESH_REASON_PRIORITY[currentReason];
  const incomingPriority = REFRESH_REASON_PRIORITY[incomingReason];
  return incomingPriority >= currentPriority ? incomingReason : currentReason;
};

const coalescePendingSchedulerRefreshRequest = (
  currentRequest: SchedulerRefreshRequest | null,
  incomingRequest: SchedulerRefreshRequest
): SchedulerRefreshRequest => {
  if (!currentRequest) return incomingRequest;
  return coalescePendingSchedulerRefreshReason(currentRequest.reason, incomingRequest.reason) === incomingRequest.reason
    ? incomingRequest
    : currentRequest;
};

export class SchedulerRefreshController {
  private readonly refresh: SchedulerRefreshControllerOptions['refresh'];
  private readonly getUnitIds: SchedulerRefreshControllerOptions['getUnitIds'];
  private readonly now: () => number;
  private readonly diagnostics?: SchedulerRefreshControllerOptions['diagnostics'];
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;
  private readonly timeoutMs: number;
  private readonly onTimeoutCleanup?: SchedulerRefreshControllerOptions['onTimeoutCleanup'];
  private refreshPromise: Promise<void> | null = null;
  private pendingRequest: SchedulerRefreshRequest | null = null;
  private lastRefreshAtValue = 0;
  private lastReasonValue: SchedulerRefreshReason | null = null;
  private postMutationCoalesce: {
    promise: Promise<void>;
    resolve: (value: void | PromiseLike<void>) => void;
    reject: (reason?: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  private currentReasonValue: SchedulerRefreshReason | null = null;
  private startedAtValue: number | null = null;
  private lastCompletedAtValue: number | null = null;
  private lastFailedAtValue: number | null = null;
  private lastTimeoutAtValue: number | null = null;
  private lastDurationMsValue: number | null = null;
  private lastSkipReasonValue: SchedulerRefreshReason | null = null;

  constructor(options: SchedulerRefreshControllerOptions) {
    this.refresh = options.refresh;
    this.getUnitIds = options.getUnitIds;
    this.now = options.now || (() => Date.now());
    this.setTimeoutFn = options.setTimeoutFn ?? ((callback, ms) => globalThis.setTimeout(callback, ms));
    this.clearTimeoutFn = options.clearTimeoutFn ?? (timerId => globalThis.clearTimeout(timerId));
    this.timeoutMs = options.timeoutMs ?? SCHEDULER_REFRESH_TIMEOUT_MS;
    this.onTimeoutCleanup = options.onTimeoutCleanup;
    this.diagnostics = options.diagnostics;
  }

  get refreshInFlight(): boolean {
    return this.refreshPromise !== null;
  }

  get lastRefreshAt(): number {
    return this.lastRefreshAtValue;
  }

  get lastReason(): SchedulerRefreshReason | null {
    return this.lastReasonValue;
  }

  isFocusRefreshStale(): boolean {
    return this.lastRefreshAtValue === 0 || this.now() - this.lastRefreshAtValue > FOCUS_STALE_MS;
  }

  isPollRefreshStale(): boolean {
    return this.lastRefreshAtValue === 0 || this.now() - this.lastRefreshAtValue > POLL_BACKOFF_MS;
  }

  getDiagnosticsState(): SchedulerRefreshDiagnosticsState {
    return {
      inFlight: this.refreshInFlight,
      currentReason: this.currentReasonValue,
      pendingReason: this.pendingRequest?.reason ?? null,
      startedAt: this.startedAtValue,
      lastCompletedAt: this.lastCompletedAtValue,
      lastFailedAt: this.lastFailedAtValue,
      lastTimeoutAt: this.lastTimeoutAtValue,
      lastDurationMs: this.lastDurationMsValue,
      lastSkipReason: this.lastSkipReasonValue,
    };
  }

  dispose(): void {
    this.cancelPostMutationCoalesce(new SchedulerRefreshCancelledError());
  }

  requestRefresh(reason: SchedulerRefreshReason, metadata: SchedulerRefreshRequestMetadata = {}): Promise<void> {
    const affectedUnitIds = this.getUnitIds();
    const request: SchedulerRefreshRequest = { reason, ...metadata };

    if (reason === 'post_mutation' && !this.refreshPromise) {
      return this.requestCoalescedPostMutation(affectedUnitIds);
    }

    const coalescedPostMutation = this.flushPostMutationCoalesceIntoPending();
    const refreshReason = coalescedPostMutation && !this.refreshPromise
      ? coalescePendingSchedulerRefreshReason(reason, 'post_mutation')
      : reason;
    const refreshRequest: SchedulerRefreshRequest = refreshReason === reason ? request : { reason: refreshReason };
    const refreshPromise = this.startRefreshNow(refreshRequest, affectedUnitIds);
    coalescedPostMutation?.resolve(refreshPromise);
    return refreshPromise;
  }

  private requestCoalescedPostMutation(affectedUnitIds: string[]): Promise<void> {
    if (this.postMutationCoalesce) {
      this.diagnostics?.({ type: 'deduped', reason: 'post_mutation', affectedUnitIds });
      return this.postMutationCoalesce.promise;
    }

    let resolveCoalesced!: (value: void | PromiseLike<void>) => void;
    let rejectCoalesced!: (reason?: unknown) => void;
    const promise = new Promise<void>((resolve, reject) => {
      resolveCoalesced = resolve;
      rejectCoalesced = reject;
    });
    promise.catch(() => undefined);
    const timer = this.setTimeoutFn(() => {
      const coalesced = this.postMutationCoalesce;
      this.postMutationCoalesce = null;
      this.startRefreshNow({ reason: 'post_mutation' }, affectedUnitIds).then(coalesced?.resolve, coalesced?.reject);
    }, POST_MUTATION_COALESCE_MS);

    this.postMutationCoalesce = {
      promise,
      resolve: resolveCoalesced,
      reject: rejectCoalesced,
      timer,
    };
    return promise;
  }

  private flushPostMutationCoalesceIntoPending(): typeof this.postMutationCoalesce {
    const coalesced = this.postMutationCoalesce;
    if (!coalesced) return null;
    this.clearTimeoutFn(coalesced.timer);
    this.postMutationCoalesce = null;
    this.pendingRequest = coalescePendingSchedulerRefreshRequest(this.pendingRequest, { reason: 'post_mutation' });
    this.diagnostics?.({ type: 'deduped', reason: 'post_mutation', affectedUnitIds: this.getUnitIds() });
    return coalesced;
  }

  private cancelPostMutationCoalesce(error: Error): void {
    const coalesced = this.postMutationCoalesce;
    if (!coalesced) return;
    this.clearTimeoutFn(coalesced.timer);
    this.postMutationCoalesce = null;
    coalesced.reject(error);
  }

  private startRefreshNow(request: SchedulerRefreshRequest, affectedUnitIds: string[]): Promise<void> {
    const { reason } = request;
    if (this.refreshPromise) {
      if (this.currentReasonValue === reason && this.pendingRequest === null) {
        this.diagnostics?.({ type: 'deduped', reason, affectedUnitIds });
        return this.refreshPromise;
      }
      this.pendingRequest = coalescePendingSchedulerRefreshRequest(this.pendingRequest, request);
      this.diagnostics?.({ type: 'deduped', reason, affectedUnitIds });
      return this.refreshPromise;
    }

    if (this.shouldSkipRefreshReason(reason)) {
      this.lastSkipReasonValue = reason;
      this.diagnostics?.({ type: 'skipped', reason, affectedUnitIds });
      return Promise.resolve();
    }

    this.refreshPromise = this.runRefreshQueue(request).finally(() => {
      this.refreshPromise = null;
    });
    this.refreshPromise.catch(() => undefined);
    return this.refreshPromise;
  }

  private async runRefreshQueue(initialRequest: SchedulerRefreshRequest): Promise<void> {
    let request: SchedulerRefreshRequest | null = initialRequest;
    while (request) {
      this.pendingRequest = null;
      const { reason } = request;
      if (this.shouldSkipRefreshReason(reason)) {
        this.lastSkipReasonValue = reason;
        this.diagnostics?.({ type: 'skipped', reason, affectedUnitIds: this.getUnitIds() });
        request = this.pendingRequest;
        continue;
      }
      await this.runSingleRefresh(request);
      request = this.pendingRequest;
    }
  }

  private shouldSkipRefreshReason(reason: SchedulerRefreshReason): boolean {
    if ((reason === 'tab_focus' || reason === 'visibility_return') && !this.isFocusRefreshStale()) {
      return true;
    }
    return reason === 'poll' && !this.isPollRefreshStale();
  }

  private async runSingleRefresh(request: SchedulerRefreshRequest): Promise<void> {
    const { reason, requestSequence } = request;
    const affectedUnitIds = this.getUnitIds();
    const startedAt = this.now();
    this.lastReasonValue = reason;
    this.currentReasonValue = reason;
    this.startedAtValue = startedAt;
    this.diagnostics?.({ type: 'start', reason, affectedUnitIds });
    try {
      const abortController = new AbortController();
      await this.withTimeout(
        this.refresh({ reason, requestSequence, unitIds: affectedUnitIds, signal: abortController.signal }),
        reason,
        affectedUnitIds,
        startedAt,
        abortController
      );
      this.lastRefreshAtValue = this.now();
      this.lastCompletedAtValue = this.lastRefreshAtValue;
      this.lastDurationMsValue = this.now() - startedAt;
      this.diagnostics?.({ type: 'complete', reason, duration: this.lastDurationMsValue, affectedUnitIds });
    } catch (error) {
      this.lastFailedAtValue = this.now();
      this.lastDurationMsValue = this.now() - startedAt;
      this.diagnostics?.({ type: 'failed', reason, duration: this.lastDurationMsValue, affectedUnitIds });
      throw error;
    } finally {
      this.currentReasonValue = null;
      this.startedAtValue = null;
    }
  }

  private withTimeout(
    refreshPromise: Promise<void>,
    reason: SchedulerRefreshReason,
    affectedUnitIds: string[],
    startedAt: number,
    abortController: AbortController
  ): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    refreshPromise.catch(() => undefined);
    const timeoutPromise = new Promise<void>((_, reject) => {
      timeoutId = this.setTimeoutFn(() => {
        this.lastTimeoutAtValue = this.now();
        this.lastDurationMsValue = this.now() - startedAt;
        this.onTimeoutCleanup?.(reason);
        this.diagnostics?.({ type: 'timeout', reason, duration: this.lastDurationMsValue, affectedUnitIds });
        reject(new SchedulerRefreshTimeoutError(reason, this.timeoutMs));
        abortController.abort();
      }, this.timeoutMs);
    });
    return Promise.race([refreshPromise, timeoutPromise]).finally(() => {
      if (timeoutId) this.clearTimeoutFn(timeoutId);
    });
  }
}
